import React, { useEffect, useMemo } from "react";
import { Trans, useTranslation } from "react-i18next";
import { Info } from "lucide-react";
import { CardSelector } from "@/components/composed/CardSelector";

import { MoneyInput } from "@/components/ui/money-input";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
	calculateBonoMivivienda,
	getValidPriceRange,
	getMatchingBonoRange,
} from "@/features/mortgages/utils/calculations";

import { TipoBono, tipoBonoSchema } from "@schemas/common";

export type BonoType = TipoBono;

interface BonoMiviviendaSelectorProps {
	price: number;
	moneda: string;
	acquisitionDate?: string;
	selectedType: BonoType;
	onChange: (type: BonoType, totalBono: number) => void;
	gridCols?: number;
	showOtro?: boolean;
	manualBonus?: number;
	error?: string;
	isReadOnly?: boolean;
}

/**
 * BonoMiviviendaSelector - Reusable component for MiVivienda bonus selection
 * Encapsulates calculation logic and UI.
 * Uses acquisition year to determine applicable bonus amounts.
 */
const BonoMiviviendaSelector: React.FC<BonoMiviviendaSelectorProps> = ({
	price,
	moneda,
	acquisitionDate,
	selectedType,
	onChange,
	gridCols = 3,
	showOtro = false,
	manualBonus = 0,
	error,
	isReadOnly = false,
}) => {
	const { t } = useTranslation(["common", "propiedades", "hipotecas"]);

	// Extract year from acquisition date
	const acquisitionYear = useMemo(() => {
		if (!acquisitionDate) return new Date().getFullYear();
		// Parse date string directly to avoid timezone issues
		// Date format is YYYY-MM-DD
		const yearMatch = acquisitionDate.match(/^(\d{4})/);
		if (yearMatch) {
			return parseInt(yearMatch[1], 10);
		}
		// Fallback to Date parsing if format is unexpected
		const year = new Date(acquisitionDate).getFullYear();
		return year;
	}, [acquisitionDate]);

	// Get valid price range for the acquisition year
	const priceRange = useMemo(() => {
		return getValidPriceRange(acquisitionYear);
	}, [acquisitionYear]);

	const minBonoPrice = priceRange?.min || 0;
	const maxBonoPrice = priceRange?.max || 0;

	const isEligible =
		moneda === "PEN" &&
		priceRange &&
		Number(price) >= minBonoPrice &&
		Number(price) <= maxBonoPrice;

	const matchingRange = useMemo(() => {
		return getMatchingBonoRange(acquisitionYear, Number(price));
	}, [acquisitionYear, price]);

	// Calculate bonuses based on price and acquisition year
	const bonuses = useMemo(() => {
		const numericPrice = Number(price);
		const bbp = calculateBonoMivivienda(numericPrice, acquisitionYear, false);
		const verde = calculateBonoMivivienda(numericPrice, acquisitionYear, true);
		return { bbp, verde };
	}, [price, acquisitionYear]);

	// Re-sync bono when the calculated amounts change (e.g. property price edited).
	// User type-selection is already handled synchronously in handleTypeChange.
	useEffect(() => {
		let expectedBono = 0;
		if (selectedType === "buen_pagador") {
			expectedBono = bonuses.bbp;
		} else if (selectedType === "verde") {
			expectedBono = bonuses.verde;
		} else {
			return; // no_bono and otro don't need syncing
		}

		if (manualBonus !== expectedBono) {
			onChange(selectedType, expectedBono);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [bonuses]);

	const options = useMemo(() => {
		const opts: {
			value: BonoType;
			label: string;
			description: string;
			amount: number;
			disabled?: boolean;
		}[] = [
			{
				value: tipoBonoSchema.enum.no_bono,
				label: t("propiedades:create.form.bono_options.none.label"),
				description: t("propiedades:create.form.bono_options.none.description"),
				amount: 0,
			},
			{
				value: tipoBonoSchema.enum.buen_pagador,
				label: t("propiedades:create.form.bono_options.tradicional.label"),
				description: t(
					"propiedades:create.form.bono_options.tradicional.description",
				),
				amount: bonuses.bbp,
				disabled: !isEligible,
			},
			{
				value: tipoBonoSchema.enum.verde,
				label: t("propiedades:create.form.bono_options.verde.label"),
				description: t(
					"propiedades:create.form.bono_options.verde.description",
				),
				amount: bonuses.verde,
				disabled: !isEligible,
			},
		];

		if (showOtro) {
			opts.push({
				value: tipoBonoSchema.enum.otro,
				label: t("hipotecas:create.bonus_other"),
				description: t("hipotecas:create.bonus_other_description"),
				amount: manualBonus, // Show current manual bonus value
			});
		}

		return opts;
	}, [t, bonuses, isEligible, showOtro, manualBonus]);

	const handleTypeChange = (value: string) => {
		const type = value as BonoType;
		let totalBono = 0;

		if (type === "buen_pagador") {
			totalBono = bonuses.bbp;
		} else if (type === "verde") {
			totalBono = bonuses.verde;
		} else if (type === "otro") {
			totalBono = manualBonus;
		}

		onChange(type, totalBono);
	};

	const handleManualChange = (val: number) => {
		onChange("otro", val);
	};

	const renderTitle = () => (
		<div className="mb-3 space-y-2">
			<div className="flex items-center gap-2">
				<h3 className="heading-md">
					{t("propiedades:create.form.bono_mivivienda")}
				</h3>
			</div>
			<p className="text-sm text-gray-500 dark:text-gray-400">
				{isEligible && matchingRange ? (
					<Trans
						i18nKey="propiedades:create.form.bono_mivivienda_description"
						values={{
							min: matchingRange.minPrice.toLocaleString(),
							max: matchingRange.maxPrice.toLocaleString(),
						}}
						components={{
							bold: (
								<strong className="font-semibold text-gray-900 dark:text-white" />
							),
						}}
					/>
				) : (
					<Trans
						i18nKey="propiedades:create.form.bono_mivivienda_description_out_of_range"
						values={{
							min: minBonoPrice.toLocaleString(),
							max: maxBonoPrice.toLocaleString(),
						}}
						components={{
							bold: (
								<strong className="font-semibold text-gray-900 dark:text-white" />
							),
						}}
					/>
				)}
			</p>
			{/* Alerts informativos estáticos: siempre visibles al abrir el bloque */}
			<div className="space-y-2">
				<Alert variant="info">
					<Info className="size-5" />
					<AlertDescription>
						<Trans
							i18nKey="propiedades:create.form.bono_mivivienda_alert_compra_deuda"
							components={{
								bold: <strong className="font-semibold" />,
							}}
						/>
					</AlertDescription>
				</Alert>
				<Alert variant="info">
					<Info className="size-5" />
					<AlertDescription>
						{t("propiedades:create.form.bono_mivivienda_alert_amortizar")}
					</AlertDescription>
				</Alert>
			</div>
		</div>
	);

	return (
		<div className="space-y-4">
			<div className="rounded-3xl border border-default p-4 dark:border-slate-700">
				<CardSelector
					value={selectedType}
					onChange={handleTypeChange}
					options={options}
					title={renderTitle()}
					currency={moneda === "USD" ? "$" : moneda === "EUR" ? "€" : "S/"}
					gridCols={gridCols}
					disabled={isReadOnly}
				/>
			</div>

			{selectedType === "otro" && (
				<div className="space-y-2">
					<label className="text-sm font-medium text-gray-700 dark:text-gray-300">
						{t("hipotecas:create.bonus_amount")}
					</label>
					<MoneyInput
						value={manualBonus}
						onValueChange={(values) =>
							handleManualChange(values.floatValue || 0)
						}
						disabled={isReadOnly}
						placeholder={t(
							"propiedades:create.form.bono_buen_pagador_placeholder",
						)}
					/>
					{error && <p className="text-sm text-red-500">{error}</p>}
				</div>
			)}
		</div>
	);
};

export default BonoMiviviendaSelector;
